import React, { useEffect, useRef, useState } from 'react';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  Time,
  CandlestickData,
  LineData,
  SeriesMarker,
  CandlestickSeriesOptions,
  LineSeriesOptions,
  CandlestickSeries,
  LineSeries,
} from 'lightweight-charts';
import './LiveBotChart.css';

interface LiveTrade {
  id: string;
  time: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  pnl?: number;
}

interface PricePoint {
  time: number;
  value: number;
}

interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface GridLevels {
  low: number;
  high: number;
  count: number;
}

type ChartType = 'candlestick' | 'line';
type TimeFrame = '1m' | '5m' | '15m' | '1h' | '4h' | '1D';

type TradingStatus =
  | 'normal_trading'
  | 'pre_market'
  | 'post_market'
  | 'closed'
  | 'break'
  | 'opening_auction'
  | 'closing_auction'
  | 'dealer_normal'
  | 'unknown';

interface LiveBotChartProps {
  priceData?: PricePoint[];
  candles?: Candle[];
  trades: LiveTrade[];
  gridLevels?: GridLevels; // Active bot grid levels (solid green)
  previewGridLevels?: GridLevels; // Preview grid levels during config (dashed blue)
  height?: number;
  chartType?: ChartType;
  timeframe?: TimeFrame;
  onTimeframeChange?: (tf: TimeFrame) => void;
  isLoading?: boolean;
  onLoadMore?: (beforeTimestamp: number) => void; // Lazy loading callback
  isLoadingMore?: boolean;
  symbol?: string; // Current asset symbol (used to reset scroll on asset change)
  symbolName?: string; // Display name of the symbol
  tradingStatus?: TradingStatus; // Market trading status
  noDataMessage?: string; // Custom message when no data available
  onDownloadData?: () => void; // Callback to trigger data download
  /** Real-time candle update - uses chart.update() for efficiency */
  realtimeCandle?: Candle | null;
}

const TIMEFRAMES: { value: TimeFrame; label: string }[] = [
  { value: '1m', label: '1м' },
  { value: '5m', label: '5м' },
  { value: '15m', label: '15м' },
  { value: '1h', label: '1ч' },
  { value: '4h', label: '4ч' },
  { value: '1D', label: '1Д' },
];

// Trading status labels and styles
const TRADING_STATUS_CONFIG: Record<TradingStatus, { label: string; color: string; bgColor: string }> = {
  normal_trading: { label: 'Торги идут', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.15)' },
  pre_market: { label: 'Премаркет', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  post_market: { label: 'Постмаркет', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  closed: { label: 'Закрыто', color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.15)' },
  break: { label: 'Перерыв', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  opening_auction: { label: 'Аукцион открытия', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  closing_auction: { label: 'Аукцион закрытия', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  dealer_normal: { label: 'Дилерский режим', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  unknown: { label: '—', color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.15)' },
};

const LiveBotChart: React.FC<LiveBotChartProps> = ({
  priceData = [],
  candles = [],
  trades,
  gridLevels,
  previewGridLevels,
  height = 400,
  chartType = 'candlestick',
  timeframe = '1h',
  onTimeframeChange,
  isLoading = false,
  onLoadMore,
  isLoadingMore = false,
  symbol,
  symbolName,
  tradingStatus,
  noDataMessage,
  onDownloadData,
  realtimeCandle,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const gridLinesRef = useRef<any[]>([]);
  const previewGridLinesRef = useRef<any[]>([]);
  const [internalChartType, setInternalChartType] = useState<ChartType>(chartType);
  const loadMoreTriggeredRef = useRef<number>(0); // Prevent duplicate triggers
  const isInitialLoadRef = useRef<boolean>(true); // Track if this is initial load
  const [chartReady, setChartReady] = useState(0); // Increments when chart is created

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#0a0a0a' },
        textColor: '#e5e7eb',
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      width: chartContainerRef.current.clientWidth,
      height,
      timeScale: {
        timeVisible: true,
        secondsVisible: timeframe === '1m',
        borderColor: '#2a2a2a',
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      crosshair: {
        mode: 1,
      },
    });

    chartRef.current = chart;
    setChartReady((prev) => prev + 1); // Signal that chart is ready

    // Responsive resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        chart.applyOptions({ width });
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      lineSeriesRef.current = null;
      gridLinesRef.current = [];
    };
  }, [height, timeframe]);

  // Refs to track current values without causing effect re-runs
  const candlesRef = useRef(candles);
  const isLoadingMoreRef = useRef(isLoadingMore);

  // Keep refs in sync
  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Reset state when timeframe or symbol changes
  useEffect(() => {
    loadMoreTriggeredRef.current = 0;
    isInitialLoadRef.current = true; // Reset to scroll to latest on timeframe/symbol change
  }, [timeframe, symbol]);

  // Lazy loading: detect when user scrolls to the left edge
  useEffect(() => {
    if (!chartRef.current || !onLoadMore) return;

    const chart = chartRef.current;
    const timeScale = chart.timeScale();

    const handleVisibleRangeChange = () => {
      // Use refs to get current values
      const currentCandles = candlesRef.current;
      const currentIsLoading = isLoadingMoreRef.current;

      // Skip if already loading or no candles
      if (currentIsLoading || currentCandles.length === 0) return;

      const visibleRange = timeScale.getVisibleLogicalRange();
      if (!visibleRange) return;

      // If user scrolled close to the left edge
      const leftEdgeThreshold = 30; // Trigger when within 30 bars of the start

      if (visibleRange.from <= leftEdgeThreshold) {
        // Get earliest candle time from current candles array
        const earliestCandle = currentCandles[0];
        if (!earliestCandle) return;

        const earliestTime = earliestCandle.time;

        // Prevent duplicate triggers for the same timestamp
        if (loadMoreTriggeredRef.current === earliestTime) {
          return;
        }
        loadMoreTriggeredRef.current = earliestTime;

        onLoadMore(earliestTime);
      }
    };

    timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    return () => {
      timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
    };
    // Re-subscribe when chart is recreated (chartReady changes when chart is created)
  }, [onLoadMore, chartReady]);

  // Create/switch series based on chart type
  useEffect(() => {
    if (!chartRef.current) return;

    // Remove existing series
    if (candleSeriesRef.current) {
      chartRef.current.removeSeries(candleSeriesRef.current);
      candleSeriesRef.current = null;
    }
    if (lineSeriesRef.current) {
      chartRef.current.removeSeries(lineSeriesRef.current);
      lineSeriesRef.current = null;
    }
    gridLinesRef.current = [];

    if (internalChartType === 'candlestick') {
      const candleSeries = chartRef.current.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      } as CandlestickSeriesOptions);
      candleSeriesRef.current = candleSeries;
    } else {
      const lineSeries = chartRef.current.addSeries(LineSeries, {
        color: '#3b82f6',
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
      } as LineSeriesOptions);
      lineSeriesRef.current = lineSeries;
    }
    // Re-create series when chart is recreated (chartReady changes when chart is created)
  }, [internalChartType, chartReady]);

  // Update candlestick data - ONLY when candles change (not trades!)
  // This follows TradingView best practice: setData() only on initial load/historical data changes
  useEffect(() => {
    if (!candleSeriesRef.current || candles.length === 0) return;

    // Calculate timezone offset (convert UTC to local time)
    // getTimezoneOffset returns minutes, negative for timezones ahead of UTC (e.g., -180 for UTC+3)
    const timezoneOffsetSec = -new Date().getTimezoneOffset() * 60;

    // Deduplicate and sort candles
    const byTime = new Map<number, Candle>();
    for (const c of candles) {
      byTime.set(c.time, c);
    }

    const candleData: CandlestickData[] = Array.from(byTime.values())
      .sort((a, b) => a.time - b.time)
      .map((c) => ({
        time: (c.time + timezoneOffsetSec) as Time, // Adjust for local timezone
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }));

    if (candleData.length === 0) return;

    candleSeriesRef.current.setData(candleData);

    // Auto-scroll to latest only on initial load, not during lazy loading
    if (isInitialLoadRef.current) {
      chartRef.current?.timeScale().scrollToRealTime();
      isInitialLoadRef.current = false;
    }
  }, [candles]); // CRITICAL: Only depend on candles, NOT trades!

  // Update trade markers - separate effect to avoid triggering setData()
  useEffect(() => {
    if (!candleSeriesRef.current || candles.length === 0) return;

    const timezoneOffsetSec = -new Date().getTimezoneOffset() * 60;

    // Build sorted array of candle times (adjusted for timezone)
    const candleTimesSet = new Set(candles.map((c) => c.time + timezoneOffsetSec));
    const sortedCandleTimes = Array.from(candleTimesSet).sort((a, b) => a - b);
    const minTime = sortedCandleTimes[0];
    const maxTime = sortedCandleTimes[sortedCandleTimes.length - 1];

    // Helper to find the closest candle time for a trade
    const findClosestCandleTime = (tradeTime: number): number => {
      // Binary search for the closest candle time
      let left = 0;
      let right = sortedCandleTimes.length - 1;

      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (sortedCandleTimes[mid] < tradeTime) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }

      // Check if we found exact match or need to compare neighbors
      if (left === 0) return sortedCandleTimes[0];
      if (left >= sortedCandleTimes.length) return sortedCandleTimes[sortedCandleTimes.length - 1];

      // Return the candle time that the trade falls into (floor)
      const prev = sortedCandleTimes[left - 1];
      const curr = sortedCandleTimes[left];
      return tradeTime >= curr ? curr : prev;
    };

    // Add trade markers to candlestick series
    const markers: SeriesMarker<Time>[] = trades
      .filter((trade) => {
        const adjustedTradeTime = trade.time + timezoneOffsetSec;
        // Include trades within candle range (slightly extend to catch edge cases)
        return adjustedTradeTime >= minTime - 60 && adjustedTradeTime <= maxTime + 3600;
      })
      .map((trade) => {
        const adjustedTradeTime = trade.time + timezoneOffsetSec;
        // Snap trade time to the closest candle time
        const snappedTime = findClosestCandleTime(adjustedTradeTime);
        return {
          time: snappedTime as Time,
          position: trade.type === 'buy' ? ('belowBar' as const) : ('aboveBar' as const),
          color: trade.type === 'buy' ? '#22c55e' : '#ef4444',
          shape: trade.type === 'buy' ? ('arrowUp' as const) : ('arrowDown' as const),
          text:
            trade.type === 'buy'
              ? `BUY ${trade.price.toFixed(2)}`
              : `SELL ${trade.price.toFixed(2)}${trade.pnl !== undefined ? ` (${trade.pnl > 0 ? '+' : ''}${trade.pnl.toFixed(0)})` : ''}`,
        };
      });

    markers.sort((a, b) => (a.time as number) - (b.time as number));

    console.log('[Chart] Trade markers:', {
      tradesCount: trades.length,
      markersCount: markers.length,
      candleRange: { minTime, maxTime },
      trades: trades.map(t => ({ time: t.time, type: t.type, adjustedTime: t.time + timezoneOffsetSec })),
      markers: markers.map(m => ({ time: m.time, text: m.text })),
    });

    (candleSeriesRef.current as any).setMarkers(markers);
  }, [candles, trades]); // Markers update is cheap, so trades dependency is OK here

  // Real-time candle update - uses update() for efficiency instead of setData()
  useEffect(() => {
    if (!candleSeriesRef.current || !realtimeCandle) return;

    // Calculate timezone offset
    const timezoneOffsetSec = -new Date().getTimezoneOffset() * 60;

    // Update the live candle efficiently
    candleSeriesRef.current.update({
      time: (realtimeCandle.time + timezoneOffsetSec) as Time,
      open: realtimeCandle.open,
      high: realtimeCandle.high,
      low: realtimeCandle.low,
      close: realtimeCandle.close,
    });
  }, [realtimeCandle]);

  // Update line data (fallback mode)
  useEffect(() => {
    if (!lineSeriesRef.current || priceData.length === 0) return;

    // Calculate timezone offset (convert UTC to local time)
    const timezoneOffsetSec = -new Date().getTimezoneOffset() * 60;

    // Deduplicate
    const byTime = new Map<number, number>();
    for (const p of priceData) {
      byTime.set(p.time, p.value);
    }
    const lineData: LineData[] = Array.from(byTime.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([time, value]) => ({ time: (time + timezoneOffsetSec) as Time, value })); // Adjust for local timezone

    if (lineData.length === 0) return;

    lineSeriesRef.current.setData(lineData);

    // Add trade markers
    const markers: SeriesMarker<Time>[] = trades.map((trade) => ({
      time: (trade.time + timezoneOffsetSec) as Time, // Adjust for local timezone
      position: trade.type === 'buy' ? ('belowBar' as const) : ('aboveBar' as const),
      color: trade.type === 'buy' ? '#22c55e' : '#ef4444',
      shape: trade.type === 'buy' ? ('arrowUp' as const) : ('arrowDown' as const),
      text:
        trade.type === 'buy'
          ? `BUY ${trade.price.toFixed(2)}`
          : `SELL ${trade.price.toFixed(2)}${trade.pnl !== undefined ? ` (${trade.pnl > 0 ? '+' : ''}${trade.pnl.toFixed(0)})` : ''}`,
    }));

    markers.sort((a, b) => (a.time as number) - (b.time as number));
    (lineSeriesRef.current as any).setMarkers(markers);

    // Auto-scroll to latest only on initial load
    if (isInitialLoadRef.current) {
      chartRef.current?.timeScale().scrollToRealTime();
      isInitialLoadRef.current = false;
    }
  }, [priceData, trades]);

  // Update active grid levels (from running bot - solid green)
  useEffect(() => {
    const series = candleSeriesRef.current || lineSeriesRef.current;
    if (!series) return;

    // Remove old grid lines
    for (const line of gridLinesRef.current) {
      try {
        series.removePriceLine(line);
      } catch {
        // line may already be removed
      }
    }
    gridLinesRef.current = [];

    if (!gridLevels || gridLevels.count < 2) return;

    const step = (gridLevels.high - gridLevels.low) / (gridLevels.count - 1);

    for (let i = 0; i < gridLevels.count; i++) {
      const price = gridLevels.low + step * i;
      const priceLine = series.createPriceLine({
        price,
        color: 'rgba(34, 197, 94, 0.5)', // Green for active bot
        lineWidth: 1,
        lineStyle: 0, // Solid
        axisLabelVisible: false,
        title: '',
      });
      gridLinesRef.current.push(priceLine);
    }
  }, [gridLevels, internalChartType, chartReady]);

  // Update preview grid levels (during config - dashed blue)
  useEffect(() => {
    const series = candleSeriesRef.current || lineSeriesRef.current;
    if (!series) return;

    // Remove old preview lines
    for (const line of previewGridLinesRef.current) {
      try {
        series.removePriceLine(line);
      } catch {
        // line may already be removed
      }
    }
    previewGridLinesRef.current = [];

    // Don't show preview if active grid exists (bot is running)
    if (gridLevels || !previewGridLevels || previewGridLevels.count < 2) return;

    const step = (previewGridLevels.high - previewGridLevels.low) / (previewGridLevels.count - 1);

    for (let i = 0; i < previewGridLevels.count; i++) {
      const price = previewGridLevels.low + step * i;
      const priceLine = series.createPriceLine({
        price,
        color: 'rgba(59, 130, 246, 0.3)', // Blue for preview
        lineWidth: 1,
        lineStyle: 2, // Dashed
        axisLabelVisible: false,
        title: '',
      });
      previewGridLinesRef.current.push(priceLine);
    }
  }, [previewGridLevels, gridLevels, internalChartType, chartReady]);

  const handleChartTypeChange = (type: ChartType) => {
    setInternalChartType(type);
  };

  const hasData = internalChartType === 'candlestick' ? candles.length > 0 : priceData.length > 0;

  const statusConfig = tradingStatus ? TRADING_STATUS_CONFIG[tradingStatus] : null;

  return (
    <div className="live-bot-chart">
      {/* Chart controls */}
      <div className="chart-controls">
        {/* Left: Symbol info & Trading status */}
        <div className="chart-controls-left">
          {symbolName && (
            <div className="chart-symbol-info">
              <span className="chart-symbol-name">{symbolName}</span>
            </div>
          )}
          {statusConfig && (
            <div
              className="trading-status-badge"
              style={{
                color: statusConfig.color,
                backgroundColor: statusConfig.bgColor,
              }}
            >
              <span
                className="trading-status-dot"
                style={{ backgroundColor: statusConfig.color }}
              />
              {statusConfig.label}
            </div>
          )}
        </div>

        {/* Center: Timeframe selector */}
        <div className="timeframe-selector">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              className={`tf-btn ${timeframe === tf.value ? 'active' : ''}`}
              onClick={() => onTimeframeChange?.(tf.value)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {/* Right: Chart type selector */}
        <div className="chart-type-selector">
          <button
            className={`type-btn ${internalChartType === 'candlestick' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('candlestick')}
            title="Свечной график"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="6" width="4" height="12" rx="1" />
              <rect x="10" y="2" width="4" height="20" rx="1" />
              <rect x="16" y="8" width="4" height="8" rx="1" />
            </svg>
          </button>
          <button
            className={`type-btn ${internalChartType === 'line' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('line')}
            title="Линейный график"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="2,18 8,12 14,16 22,6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart container */}
      <div ref={chartContainerRef} className="live-bot-chart-wrapper" />

      {/* Loading overlay */}
      {isLoading && (
        <div className="live-bot-chart-loading">
          <div className="loading-spinner" />
          Загрузка данных...
        </div>
      )}

      {/* Lazy loading indicator */}
      {isLoadingMore && (
        <div className="live-bot-chart-loading-more">
          <div className="loading-spinner small" />
          Загрузка истории...
        </div>
      )}

      {/* Empty state */}
      {!hasData && !isLoading && (
        <div className="live-bot-chart-empty">
          <span>{noDataMessage || 'Нет данных для отображения'}</span>
          {onDownloadData && (
            <button className="download-data-btn" onClick={onDownloadData}>
              📥 Загрузить историю
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveBotChart;
